import React from 'react';
import { Card, CardContent, CardMedia, Typography, Box, useMediaQuery } from '@mui/material';
import { theme } from '../../theme/theme';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AIAvatarOutlined from '../../assets/svg/AIAvatarOutlined';
import { useSelector } from 'react-redux';
import { selectSubscriptions } from '../../store/slices/subscriptionsSlice';

const GalleryCard = ({ item, largeCard = false, setOpenPaymentModal }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { subscription } = useSelector(selectSubscriptions);

  const activePlan = subscription.data?.[0];

  const cardStyling = {
    width: largeCard ? '400px' : '250px',
    flexGrow: 1,
    borderRadius: '8px',
    border: `1px solid #C1D0FF`,
    boxShadow: 'none',
    p: 0,
    transition: 'transform 0.3s ease-in-out',
    '& .MuiCardContent-root:last-child': {
      paddingBottom: 0
    },
    '&:hover': {
      transform: 'scale(1.02)',
      cursor: 'pointer',
      boxShadow: '1px 1px 9px rgba(0,0,0,0.4)'
    },
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row'
  };

  const navigateHandler = () => {
    if (!activePlan) {
      setOpenPaymentModal(true);
      return;
    }

    if (activePlan.plan === 'free' && !activePlan.canceled_at && !activePlan.next_billing_date) {
      setOpenPaymentModal(true);
      return;
    }

    if (activePlan.next_billing_date && new Date() > new Date(activePlan.next_billing_date)) {
      setOpenPaymentModal(true);
      return;
    }

    if (
      activePlan.status === 'incomplete' &&
      (!activePlan.canceled_at ||
        !activePlan.next_billing_date ||
        new Date() > new Date(activePlan.canceled_at))
    ) {
      setOpenPaymentModal(true);
      return;
    }

    navigate(`/home-details/${item.id}`);
  };
  
  return (
    <Card onClick={navigateHandler} sx={cardStyling}>
      {/* Image */}
      <CardMedia
        component="img"
        width="242"
        image={item.images[0]}
        alt={`Property image of ${item?.address}`}
        sx={{
          maxWidth: isMobile ? '100%' : '250px',
          maxHeight: '215px',
          objectFit: 'cover'
        }}
      />

      {/* Content */}
      <CardContent
        sx={{
          p: 0,
          pb: 0,
          width: '100%'
        }}>
        <Box
          sx={{
            borderBottom: `1px solid #C1D0FF`,
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
          {/* Price per month */}
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              lineHeight: 1
            }}
            variant="h5">
            {item.price || '₣5,500'}{' '}
            <span
              style={{
                fontSize: '12px',
                color: theme.palette.text.secondary,
                fontWeight: 400
              }}>
              {t('per month')}
            </span>
          </Typography>

          {/* Address */}
          <Typography
            sx={{
              fontSize: '12px',
              color: theme.palette.text.grey12
            }}
            variant="body1"
            color="textSecondary">
            {item?.address}
          </Typography>
        </Box>

        {/* Beds, Baths, Square Feet */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            gap: 2
          }}>
          <Typography fontSize="14px" variant="body1">
            {item.beds} {t('beds')}
          </Typography>
          <Typography fontSize="14px" variant="body1">
            {item.baths} {t('baths')}
          </Typography>
          <Typography fontSize="14px" variant="body1">
            {item.sqrFeet} {t('m2')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            p: 2,
            gap: 0.5,
            background: theme.palette.background.lightBlue,
            flexDirection: 'column'
          }}>
          <Typography
            fontSize="14px"
            variant="h6"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
            <AIAvatarOutlined color="#3E63DD" /> {t('Why this property')}:
          </Typography>
          <Typography
            fontSize="14px"
            variant="subtitle1"
            sx={{
              lineHeight: 1.5
            }}>
            "
            {t(
              'Close to your workplace, includes a balcony (a stated preference), and has lower local taxes — perfect for your budget and lifestyle'
            )}
            "
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GalleryCard;
